// Preset registry for optimization configs (constraints + objective)

export function getPresetConfig(key, overrides = {}) {
  const k = (key || 'default').toLowerCase();
  if (k === 'media') return getMediaConfig(overrides);
  return getDefaultConfig(overrides);
}

export function getDefaultConfig(overrides = {}) {
  const locMinBreak = Number(overrides?.locationMinBreakMinutes);
  const locationMinBreakMinutes = Number.isFinite(locMinBreak) && locMinBreak > 0 ? locMinBreak : 30;
  return {
    constraints: [
      { key: 'capacity', enabled: true, params: {} },
      { key: 'overlapEdges', enabled: true, params: {} },
      { key: 'linkedEvents', enabled: true, params: {} },
      // Enforce min break between different locations
      { key: 'locationMinBreak', enabled: true, params: { minBreakMinutes: locationMinBreakMinutes } },
      { key: 'weeklyHours', enabled: true, params: {} },
      { key: 'externalUseIndicator', enabled: true, params: {} },
      { key: 'externalGating', enabled: true, params: {} },
    ],
    objective: { key: 'maximizeAllocation', params: {} },
  };
}

export function getMediaConfig(overrides = {}) {
  const mediaMaxContinuousHours = Number(overrides?.mediaMaxContinuousHours);
  const maxContinuousHours = Number.isFinite(mediaMaxContinuousHours) && mediaMaxContinuousHours > 0
    ? mediaMaxContinuousHours
    : 6;
  const mediaMinBreakMinutes = Number(overrides?.mediaMinBreakMinutes);
  const minBreakMinutes = Number.isFinite(mediaMinBreakMinutes) && mediaMinBreakMinutes > 0
    ? mediaMinBreakMinutes
    : 20;
  const mediaDailyMaxHours = Number(overrides?.mediaDailyMaxHours);
  const dailyMaxHours = Number.isFinite(mediaDailyMaxHours) && mediaDailyMaxHours > 0
    ? mediaDailyMaxHours
    : 10;
  const mediaMinDailyRestHours = Number(overrides?.mediaMinDailyRestHours);
  const restMinHours = Number.isFinite(mediaMinDailyRestHours) && mediaMinDailyRestHours > 0
    ? mediaMinDailyRestHours
    : 11;
  const mediaAmplitudeMaxHours = Number(overrides?.mediaAmplitudeMaxHours);
  const amplitudeMaxHours = Number.isFinite(mediaAmplitudeMaxHours) && mediaAmplitudeMaxHours > 0
    ? mediaAmplitudeMaxHours
    : 11;
  const mediaMaxConsecutiveDays = Number(overrides?.mediaMaxConsecutiveDays);
  const maxConsecutiveDays = Number.isFinite(mediaMaxConsecutiveDays) && mediaMaxConsecutiveDays > 0
    ? mediaMaxConsecutiveDays
    : 6;
  const mediaMinWeeklyDaysOff = Number(overrides?.mediaMinWeeklyDaysOff);
  const minWeeklyDaysOff = Number.isFinite(mediaMinWeeklyDaysOff) && mediaMinWeeklyDaysOff >= 0
    ? mediaMinWeeklyDaysOff
    : 2;
  const mediaBreakPenalty = Number(overrides?.mediaBreakPenalty);
  const breakPenalty = Number.isFinite(mediaBreakPenalty) && mediaBreakPenalty >= 0 ? mediaBreakPenalty : 0.5;
  const mediaGapPenaltyPerHour = Number(overrides?.mediaGapPenaltyPerHour);
  const gapPenaltyPerHour = Number.isFinite(mediaGapPenaltyPerHour) && mediaGapPenaltyPerHour >= 0 ? mediaGapPenaltyPerHour : 1.8;
  const mediaUsersPerDayPenalty = Number(overrides?.mediaUsersPerDayPenalty);
  const usersPerDayPenalty = Number.isFinite(mediaUsersPerDayPenalty) && mediaUsersPerDayPenalty >= 0 ? mediaUsersPerDayPenalty : 0.3;
  const locMinBreak = Number(overrides?.locationMinBreakMinutes);
  const locationMinBreakMinutes = Number.isFinite(locMinBreak) && locMinBreak > 0 ? locMinBreak : 30;
  const seriesConsistencyEnabled = !!overrides?.mediaSeriesConsistencyEnabled;
  const seriesConsPenaltyRaw = Number(overrides?.mediaSeriesConsistencyPenalty);
  const seriesConsistencyPenalty = Number.isFinite(seriesConsPenaltyRaw) && seriesConsPenaltyRaw >= 0 ? seriesConsPenaltyRaw : 0.4;
  return {
    constraints: [
      { key: 'capacity', enabled: true, params: {} },
      { key: 'overlapEdges', enabled: true, params: {} },
      { key: 'linkedEvents', enabled: true, params: {} },
      // Same-location pairs exempt; different locations need min break
      { key: 'locationMinBreak', enabled: true, params: { minBreakMinutes: locationMinBreakMinutes } },
      { key: 'mediaDayWorkIndicator', enabled: true, params: {} },
      { key: 'mediaContinuousWork', enabled: true, params: { maxContinuousHours, minBreakMinutes } },
      { key: 'mediaBreakPairs', enabled: true, params: {} },
      { key: 'weeklyHours', enabled: true, params: {} },
      // Daily cap (editable in settings)
      { key: 'dailyHours', enabled: true, params: { dailyMaxHours } },
      // Working amplitude (first start to last end) hard cap
      { key: 'mediaDailyAmplitude', enabled: true, params: { amplitudeMaxHours } },
      // Min rest between consecutive working days
      { key: 'mediaDailyRest', enabled: true, params: { restMinHours } },
      // No more than N consecutive work days
      { key: 'mediaConsecutiveDays', enabled: true, params: { maxConsecutive: maxConsecutiveDays } },
      // At least K days off per week
      { key: 'mediaWeeklyDaysOff', enabled: true, params: { minDaysOff: minWeeklyDaysOff } },
      { key: 'externalUseIndicator', enabled: true, params: {} },
      { key: 'externalGating', enabled: true, params: {} },
      // Optional: series consistency linking x to ys
      { key: 'mediaSeriesConsistency', enabled: seriesConsistencyEnabled, params: {} },
    ],
    objective: {
      key: 'mediaMaximizeAllocation',
      params: {
        weights: {
          breakCount: breakPenalty,
          gapPerHour: gapPenaltyPerHour,
          usersPerDayPenalty,
          ...(seriesConsistencyEnabled ? { seriesDistinct: seriesConsistencyPenalty } : {}),
        },
      },
    },
  };
}
